import { useEffect, useRef, useState } from 'react';
import { Box, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import i18n from 'i18next';
import { formatarData } from '../../utils/formatacao';
import FormFieldFile from './FormFieldFile';

/**
 * Campo de data com validação de intervalo (dataMinima / dataMaxima).
 * Encapsula o estado de erro isoladamente por campo.
 */
function DateFieldDinamico({ campo, valor, onChange, onError, fieldSize, disabled, label }) {
  const [dateError, setDateError] = useState(null);
  const cfg = campo.configuracao || {};
  const minDate = cfg.dataMinima ? dayjs(cfg.dataMinima) : undefined;
  const maxDate = cfg.dataMaxima ? dayjs(cfg.dataMaxima) : undefined;

  return (
    <DatePicker
      label={label}
      value={valor ? dayjs(valor) : null}
      onChange={(newValue) => {
        onChange(campo.codigo, newValue?.isValid() ? newValue.format('YYYY-MM-DD') : '');
      }}
      minDate={minDate}
      maxDate={maxDate}
      onError={(reason) => {
        let msg = null;
        if (reason === 'minDate') msg = i18n.t('data.aPartirDe', { date: formatarData(minDate.toDate()) });
        else if (reason === 'maxDate') msg = i18n.t('data.ateDia', { date: formatarData(maxDate.toDate()) });
        else if (reason === 'invalidDate') msg = i18n.t('data.invalida');
        setDateError(msg);
        onError?.(campo.codigo, !!msg);
      }}
      disabled={disabled}
      inputFormat="DD/MM/YYYY"
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size={fieldSize}
          required={campo.obrigatorio}
          error={!!dateError}
          helperText={dateError}
        />
      )}
    />
  );
}

/**
 * Avalia `configuracao.forcarValorSe` do campo e retorna o valor forçado se a
 * condição está ativa; null caso contrário. Mesma lógica do backend
 * (FormularioRegrasService.AplicarForcarValorSe) e do FormularioDinamico do backoffice.
 */
function forcarValorSe(campo, valores) {
  const regra = campo?.configuracao?.forcarValorSe;
  if (!regra?.campo) return null;

  const atual = String(valores[regra.campo] ?? '');
  const esperado = regra.valor == null ? '' : String(regra.valor);

  const a = atual.trim().toLowerCase();
  const b = esperado.trim().toLowerCase();
  const isBoolLike = (s) => /^(true|false|1|0)$/i.test(s);
  const eq = a === b || (isBoolLike(a) && isBoolLike(b) && /^(true|1)$/i.test(a) === /^(true|1)$/i.test(b));

  if (!eq) return null;
  return regra.valorForcado == null ? '' : String(regra.valorForcado);
}

/**
 * Renderiza um formulário dinâmico a partir de um array de campos.
 *
 * Props:
 *   campos  — array de CampoFormulario
 *   valores — objeto { [codigo]: valor }
 *   onChange(codigo, valor) — callback ao alterar um campo
 */
export default function FormularioDinamico({
  campos = [],
  valores = {},
  onChange,
  onValidationChange,
  isMobile: isMobileProp,
  gridTemplateColumns = { xs: '1fr', sm: '1fr 1fr' },
}) {
  const theme = useTheme();
  const isMobileDetected = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isMobileProp !== undefined ? isMobileProp : isMobileDetected;
  const fieldSize = isMobile ? 'medium' : 'small';
  const sorted = [...campos].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  // Datas com erro (fora do intervalo/ inválidas), por código do campo. Só interessa
  // avisar o pai se há algum erro — ref basta, não precisa re-renderizar.
  const datasComErro = useRef({});

  const handleDateError = (codigo, hasError) => {
    datasComErro.current = { ...datasComErro.current, [codigo]: hasError };
    onValidationChange?.(Object.values(datasComErro.current).some(Boolean));
  };

  // Quando a condição `forcarValorSe` fica ativa, sincroniza o valor no estado
  // do pai (via onChange) — garante que o submit envia o valor correto mesmo se
  // o usuário nunca interagiu com o campo forçado.
  useEffect(() => {
    campos.forEach((c) => {
      const forcado = forcarValorSe(c, valores);
      if (forcado !== null && valores[c.codigo] !== forcado) {
        onChange(c.codigo, forcado);
      }
    });
  }, [campos, valores, onChange]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: gridTemplateColumns, gap: 2.5 }}>
      {sorted.map((c) => {
        const valor = valores[c.codigo] || '';
        const label = c.label || c.codigo;
        const tipo = (c.tipo || 'text').toLowerCase();
        const forcado = forcarValorSe(c, valores);
        const disabled = forcado !== null;

        // Visibilidade condicional — mesmo comportamento do backoffice e FormularioProdutor
        const regraVisivel = c.configuracao?.visivelSe;
        if (regraVisivel?.campo) {
          const currentVal = valores[regraVisivel.campo];
          const matches =
            currentVal === regraVisivel.valor || String(currentVal ?? '') === String(regraVisivel.valor ?? '');
          if (!matches) return null;
        }

        if (tipo === 'select' || tipo === 'lista') {
          return (
            <FormControl key={c.codigo} size={fieldSize} fullWidth required={c.obrigatorio} disabled={disabled}>
              <InputLabel>{label}</InputLabel>
              <Select label={label} value={valor} onChange={(e) => onChange(c.codigo, e.target.value)}>
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {(c.opcoes || []).map((o) => (
                  <MenuItem key={o.valor} value={o.valor}>
                    {o.label || o.valor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        if (tipo === 'boolean' || tipo === 'switch') {
          return (
            <FormControlLabel
              key={c.codigo}
              disabled={disabled}
              control={
                <Switch checked={valor === 'true'} onChange={(e) => onChange(c.codigo, String(e.target.checked))} />
              }
              label={label}
            />
          );
        }

        if (tipo === 'textarea') {
          const isMultiCol = gridTemplateColumns !== '1fr' && gridTemplateColumns?.sm !== '1fr';
          return (
            <Box key={c.codigo} sx={isMultiCol ? { gridColumn: { xs: '1', sm: '1 / -1' } } : undefined}>
              <TextField
                label={label}
                value={valor}
                onChange={(e) => onChange(c.codigo, e.target.value)}
                size={fieldSize}
                fullWidth
                multiline
                minRows={3}
                required={c.obrigatorio}
                placeholder={c.placeholder}
                disabled={disabled}
              />
            </Box>
          );
        }

        if (tipo === 'date') {
          return (
            <DateFieldDinamico
              key={c.codigo}
              campo={c}
              valor={valor}
              onChange={onChange}
              onError={handleDateError}
              fieldSize={fieldSize}
              disabled={disabled}
              label={label}
            />
          );
        }

        if (tipo === 'number') {
          return (
            <TextField
              key={c.codigo}
              label={label}
              type="number"
              value={valor}
              onChange={(e) => onChange(c.codigo, e.target.value)}
              size={fieldSize}
              fullWidth
              required={c.obrigatorio}
              placeholder={c.placeholder}
              disabled={disabled}
            />
          );
        }

        if (tipo === 'file') {
          return (
            <FormFieldFile
              key={c.codigo}
              campo={c}
              valor={valor}
              onChange={onChange}
              disabled={disabled}
              fieldSize={fieldSize}
            />
          );
        }

        return (
          <TextField
            key={c.codigo}
            label={label}
            value={valor}
            onChange={(e) => onChange(c.codigo, e.target.value)}
            size={fieldSize}
            fullWidth
            required={c.obrigatorio}
            placeholder={c.placeholder}
            disabled={disabled}
          />
        );
      })}
    </Box>
  );
}
